import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { navigate, PATHS } from '@client/routes';
import {
  CommunityToggleModal,
  Influencer,
} from './components/CommunityToggleModal';
import { ReactComponent as LogoSvg } from '../assets/logo.svg';
import classNames from 'classnames';
import auth from '@client/components/auth';
import _ from 'lodash';
import { DisabledCommunityState } from './components/DisabledCommunityState';
import { useCommunityUser } from './hooks/useCommunityUser';
import { RouteComponentProps } from 'react-router';
import { useCommunityIndicator } from '../hooks/use-community-indicator';
import {
  hasPremiumFeaturesOTPIds,
  isSubscriptionCreator,
} from '../common/offering.helpers';
import { BusinessModel } from '@playbook-technologies/common-enums';

type Props = { component: FunctionComponent } & RouteComponentProps<
  { influencerId?: string },
  unknown
>;

export const CommunityInitPage = (props: Props) => {
  const isAdmin = auth.isAdministrator();
  useCommunityIndicator(true);
  const { influencer, isLoading, fetchInfluencer } = useCommunityUser(
    props.params?.influencerId,
  );

  const [isCommunityModalVisible, setCommunityModalVisible] =
    useState<boolean>(false);
  const [isCommunityEnabled, setCommunityEnabled] = useState<boolean>(false);

  if (influencer?.communityId) {
    navigate(PATHS.COMMUNITY(influencer.id, influencer.communityId));
  }

  useEffect(() => {
    if (isCommunityEnabled) {
      fetchInfluencer();
    }
  }, [isCommunityEnabled, fetchInfluencer]);

  const onEnable = () => {
    setCommunityModalVisible(true);
  };

  if (
    !isSubscriptionCreator(influencer?.offering) &&
    !hasPremiumFeaturesOTPIds(influencer?.customizations)
  ) {
    return (
      <Box className="w-full h-full flex flex-col items-center justify-center">
        <Typography>Feature available only with subscription</Typography>
      </Box>
    );
  }

  return (
    <Box
      className={classNames('w-full h-full flex flex-col items-center', {
        'justify-start': !isLoading,
        'justify-center': isLoading,
      })}
    >
      {isLoading ? (
        <>
          <LogoSvg className="w-[280px] h-[60px]" />
          Loading...
        </>
      ) : (
        <>
          <Typography
            variant="h4"
            component="h1"
            className="place-self-start text-[28px] mb-[45px] font-extrabold"
          >
            {isAdmin
              ? `${influencer?.firstName} ${influencer?.lastName}'s Community`
              : 'Community'}
          </Typography>
          <DisabledCommunityState
            onEnable={onEnable}
            isBundleBusinessModel={
              influencer?.businessModel === BusinessModel.Bundle
            }
            influencerId={influencer?.id || 0}
          />
          {influencer && (
            <CommunityToggleModal
              isModalVisible={isCommunityModalVisible}
              onDismiss={() => setCommunityModalVisible(false)}
              influencer={influencer as Influencer}
              setCommunityEnabled={setCommunityEnabled}
              isCommunityEnabled={isCommunityEnabled}
              setCommunityModalVisible={setCommunityModalVisible}
            />
          )}
        </>
      )}
    </Box>
  );
};
